import { useTranslation } from 'react-i18next';
import { formatPoints } from '@kidshome/i18n';
import { cn } from '@kidshome/ui';
import { useHouseholdContext } from '@/shared/lib/household';
import { CoinIcon } from '@/shared/ui/CoinIcon';

/**
 * A coin amount chip — a localized plain integer (never currency, ADR-0022) + the pluralized "coins"
 * unit from the catalog + a coin glyph. Used wherever points are shown (balances, chore rewards).
 */
export function CoinChip({
  coins,
  size = 'md',
  withLabel = false,
  className,
}: {
  coins: number;
  size?: 'sm' | 'md' | 'lg';
  withLabel?: boolean;
  className?: string;
}): React.ReactElement {
  const { t } = useTranslation('family');
  const { locale } = useHouseholdContext();
  const value = formatPoints(locale, coins);
  // The full "N coins" label always backs the chip for assistive tech, so a screen reader announces
  // "5 coins" rather than a bare number + an undescribed coin glyph. The visible content (coin icon +
  // number, or the spelled-out label when `withLabel`) is `aria-hidden` so it isn't read twice.
  const label = t('common.coinsLabel', { count: coins });
  const text = withLabel ? label : value;
  const sizeCls =
    size === 'lg'
      ? 'text-md px-3 py-1.5'
      : size === 'sm'
        ? 'text-xs px-2 py-0.5'
        : 'text-sm px-2.5 py-1';
  return (
    <span
      role="img"
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1 rounded-pill bg-warning-fg font-semibold text-warning',
        sizeCls,
        className,
      )}
    >
      <CoinIcon />
      <span aria-hidden="true">{text}</span>
    </span>
  );
}
